import { Modal } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { IconAlertTriangle, IconArrowsExchange, IconFolderPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    ApiError,
    MoveResourceResult,
    MoveResourceType,
    ProjectModel,
} from '@xver/apis/v1/projects/model';
import { Button, InputField } from '@xver/components/Atoms';
import { invalidateProjectQueries, useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { PROJECTS_QUERY_KEY, useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import { getErrorCode } from '@xver/utils/error';
import {
    isCrossOwner,
    movableTargets,
    namedMoveResults,
    PROJECT_NAME_MAX,
    projectOptionLabel,
    SELECT_SEARCHABLE_AFTER,
    validateProjectFields,
} from '@xver/utils/projects';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MoveResourceItem {
    type: MoveResourceType;
    id: string;
    name: string;
}

interface MoveResourcesModalProps {
    opened: boolean;
    onClose: () => void;
    /** The project the resources are in now. */
    source: ProjectModel;
    resources: MoveResourceItem[];
    /** Called once the server answered with per-resource results. */
    onMoved?: (results: MoveResourceResult[]) => void;
}

const MOVE_LIMIT = 50;
const PREVIEW_COUNT = 5;

type FieldErrors = { target?: string; confirm?: string };

export default function MoveResourcesModal({
    opened,
    onClose,
    source,
    resources,
    onMoved,
}: MoveResourcesModalProps) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { data: projects } = useProjectsQuery();
    const { errorMessage } = useProjectFeedback();
    const [targetId, setTargetId] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [results, setResults] = useState<MoveResourceResult[] | null>(null);
    const [newName, setNewName] = useState('');
    const [createSubmitted, setCreateSubmitted] = useState(false);
    const [created, setCreated] = useState<ProjectModel | null>(null);
    const targetRef = useRef<HTMLInputElement>(null);
    const newNameRef = useRef<HTMLInputElement>(null);
    const focusMoveRef = useRef(false);
    const confirmRef = useRef<HTMLInputElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);

    const types = useMemo(() => [...new Set(resources.map((item) => item.type))], [resources]);
    const targets = useMemo(
        () => movableTargets(projects ?? [], source, types),
        [projects, source, types]
    );
    const target = targets.find((project) => project.id === targetId);
    const crossOwner = !!target && isCrossOwner(source, target);
    const overLimit = resources.length > MOVE_LIMIT;
    const canCreateTarget =
        !results && !!projects && targets.length === 0 && source.role === 'owner';

    const {
        mutate,
        reset: resetMove,
        ...move
    } = useMutation({
        mutationFn: () =>
            Endpoints.v1.Projects.moveResources(source.id, {
                targetProjectId: targetId,
                resources: resources.map(({ type, id }) => ({ type, id })),
            }),
        onSuccess: (response) => {
            const moved = namedMoveResults(response.results ?? [], resources);
            setResults(moved);
            onMoved?.(moved);
            void invalidateProjectQueries(queryClient, { withResources: true });
        },
    });

    const {
        mutate: createTarget,
        reset: resetCreate,
        ...create
    } = useMutation({
        mutationFn: () =>
            Endpoints.v1.Projects.createProject({ name: newName.trim(), description: '' }),
        onSuccess: (project) => {
            queryClient.setQueriesData<ProjectModel[]>({ queryKey: PROJECTS_QUERY_KEY }, (old) =>
                old && !old.some((item) => item.id === project.id) ? [...old, project] : old
            );
            focusMoveRef.current = true;
            setTargetId(project.id);
            setCreated(project);
            void invalidateProjectQueries(queryClient);
        },
        onError: (error) => {
            if (getErrorCode(error) === 'project_name_taken') newNameRef.current?.focus();
        },
    });
    const pending = move.isPending || create.isPending;
    const createdIsTarget = !!created && target?.id === created.id;

    useEffect(() => {
        if (!opened) return;
        setTargetId('');
        setConfirmation('');
        setFieldErrors({});
        setResults(null);
        setNewName('');
        setCreateSubmitted(false);
        setCreated(null);
        resetMove();
        resetCreate();
    }, [opened, resetMove, resetCreate]);

    useEffect(() => {
        if (results) footerRef.current?.querySelector('button')?.focus();
    }, [results]);

    useEffect(() => {
        if (!createdIsTarget || !focusMoveRef.current) return;
        focusMoveRef.current = false;
        footerRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus();
    }, [createdIsTarget]);

    const reasonText = ({ reason, reasonCode }: { reason?: string; reasonCode?: string }) =>
        reasonCode
            ? t(`move.results.reasons.${reasonCode}` as 'move.results.reasons.try_again', {
                  defaultValue: reason,
              })
            : reason;

    const handleClose = () => {
        if (pending) return;
        onClose();
    };

    const newNameErrorCode = validateProjectFields({ name: newName, description: '' }).name;
    const nameTaken = getErrorCode(create.error) === 'project_name_taken';
    const newNameError =
        createSubmitted && newNameErrorCode
            ? t(`form.errors.name.${newNameErrorCode}`, { max: PROJECT_NAME_MAX })
            : nameTaken
              ? errorMessage(create.error)
              : undefined;
    const createError = create.error && !nameTaken ? errorMessage(create.error) : undefined;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (pending || overLimit) return;
        if (canCreateTarget) {
            setCreateSubmitted(true);
            if (newNameErrorCode) return newNameRef.current?.focus();
            createTarget();
            return;
        }
        if (!target) {
            setFieldErrors({ target: t('move.errors.targetRequired') });
            targetRef.current?.focus();
            return;
        }
        if (crossOwner && confirmation.trim() !== target.name) {
            setFieldErrors({ confirm: t('move.errors.confirmMismatch', { name: target.name }) });
            confirmRef.current?.focus();
            return;
        }
        setFieldErrors({});
        mutate();
    };

    const groupedTargets = useMemo(() => {
        const toItem = (project: ProjectModel) => ({
            value: project.id,
            label: projectOptionLabel(project, t),
        });
        const own = targets.filter((project) => project.role === 'owner').map(toItem);
        const shared = targets.filter((project) => project.role !== 'owner').map(toItem);
        return [
            ...(own.length ? [{ group: t('switcher.ownGroup'), items: own }] : []),
            ...(shared.length ? [{ group: t('switcher.sharedGroup'), items: shared }] : []),
        ];
    }, [targets, t]);

    const busy =
        getErrorCode(move.error) === 'resource_busy' ? (move.error as unknown as ApiError) : null;
    const summary = results && {
        moved: results.filter((result) => result.status === 'moved').length,
        skipped: results.filter((result) => result.status === 'skipped').length,
        failed: results.filter((result) => result.status === 'failed').length,
    };

    return (
        <Modal
            size="lg"
            opened={opened}
            onClose={handleClose}
            centered
            closeOnClickOutside={!pending}
            closeOnEscape={!pending}
            closeButtonProps={{ 'aria-label': t('common.close'), disabled: pending }}
            title={
                <span className="text-base font-bold">
                    {t('move.title', { count: resources.length })}
                </span>
            }
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
        >
            <form
                onSubmit={handleSubmit}
                noValidate
                className="flex flex-col justify-start items-stretch gap-4"
            >
                <div className="text-sm text-fg/78">
                    <p>{t('move.from', { name: source.name })}</p>
                    <ul className="mt-1 list-disc ps-5">
                        {resources.slice(0, PREVIEW_COUNT).map((item) => (
                            <li key={`${item.type}:${item.id}`}>
                                {item.name}{' '}
                                <span className="text-fg/68">({t(`move.types.${item.type}`)})</span>
                            </li>
                        ))}
                    </ul>
                    {resources.length > PREVIEW_COUNT && (
                        <p className="mt-1">
                            {t('move.andMore', { count: resources.length - PREVIEW_COUNT })}
                        </p>
                    )}
                </div>

                {overLimit && (
                    <p role="alert" className="text-sm text-fg">
                        {t('move.errors.tooMany', { max: MOVE_LIMIT })}
                    </p>
                )}

                {!results && !!projects && targets.length === 0 && !canCreateTarget && (
                    <p className="text-sm text-fg">{t('move.noTargets')}</p>
                )}

                {canCreateTarget && (
                    <>
                        <p className="text-sm text-fg">{t('move.createIntro')}</p>
                        <InputField
                            ref={newNameRef}
                            data-autofocus
                            size="md"
                            width="100%"
                            label={t('form.nameLabel')}
                            description={t('form.nameDescription', { max: PROJECT_NAME_MAX })}
                            value={newName}
                            onChange={(event) => {
                                setNewName(event.target.value);
                                if (nameTaken) resetCreate();
                            }}
                            required
                            autoComplete="off"
                            readOnly={create.isPending}
                            error={newNameError}
                        />
                        {createError && (
                            <p role="alert" className="text-sm text-fg">
                                {createError}
                            </p>
                        )}
                    </>
                )}

                {!results && targets.length > 0 && (
                    <>
                        <Select
                            ref={targetRef}
                            data-autofocus
                            label={t('move.targetLabel')}
                            description={t('move.targetDescription')}
                            placeholder={t('move.chooseTarget')}
                            data={groupedTargets}
                            value={targetId || null}
                            allowDeselect={false}
                            searchable={targets.length > SELECT_SEARCHABLE_AFTER}
                            nothingFoundMessage={t('switcher.nothingFound')}
                            onChange={(value) => {
                                setTargetId(value ?? '');
                                setConfirmation('');
                                setFieldErrors({});
                            }}
                            error={fieldErrors.target}
                            disabled={move.isPending}
                            size="md"
                        />

                        <div aria-live="polite" className="flex flex-col gap-4">
                            {crossOwner && target && (
                                <div className="flex gap-3 p-3 rounded-md border-[0.5px] border-line bg-surface-2 text-sm">
                                    <IconAlertTriangle
                                        size={20}
                                        color="#f59e0b"
                                        className="shrink-0"
                                        aria-hidden="true"
                                    />
                                    <div className="flex flex-col gap-1">
                                        <p className="font-bold">{t('move.crossOwner.title')}</p>
                                        <ul className="list-disc ps-5 text-fg/78">
                                            <li>
                                                {t('move.crossOwner.ownership', {
                                                    owner: target.owner.name,
                                                })}
                                            </li>
                                            {types.includes('instance') && (
                                                <li>{t('move.crossOwner.privateIp')}</li>
                                            )}
                                            {types.includes('container') && (
                                                <>
                                                    <li>{t('move.crossOwner.images')}</li>
                                                    <li>{t('move.crossOwner.credentials')}</li>
                                                </>
                                            )}
                                            {types.includes('baremetal') && (
                                                <li>{t('move.crossOwner.prepaid')}</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>

                        {crossOwner && target && (
                            <InputField
                                ref={confirmRef}
                                size="md"
                                width="100%"
                                label={t('move.confirmLabel')}
                                description={t('move.confirmDescription', { name: target.name })}
                                value={confirmation}
                                onChange={(event) => {
                                    setConfirmation(event.target.value);
                                    if (fieldErrors.confirm) setFieldErrors({});
                                }}
                                readOnly={move.isPending}
                                autoComplete="off"
                                spellCheck={false}
                                error={fieldErrors.confirm}
                            />
                        )}
                    </>
                )}

                {move.isError && (
                    <div role="alert" className="text-sm text-fg">
                        <p>{errorMessage(move.error)}</p>
                        {busy?.resources && busy.resources.length > 0 && (
                            <ul className="mt-1 list-disc ps-5 text-fg/78">
                                {busy.resources.map((item) => (
                                    <li key={`${item.type}:${item.id}`}>
                                        {item.name}: {reasonText(item)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <div role="status" className="text-sm">
                    {createdIsTarget && !results && (
                        <p>{t('move.createdSelected', { name: created.name })}</p>
                    )}
                    {summary && results && (
                        <div className="flex flex-col gap-2">
                            <p className="font-bold">{t('move.results.summary', summary)}</p>
                            <ul className="flex flex-col gap-1">
                                {results.map((result) => (
                                    <li key={`${result.type}:${result.id}`}>
                                        <span className="font-medium">{result.name}</span>
                                        {': '}
                                        {t(`move.results.status.${result.status}`)}
                                        {result.reason && ` (${reasonText(result)})`}
                                        {result.warnings?.map((warning) => (
                                            <span key={warning} className="block text-fg/78">
                                                {t(`move.warnings.${warning}`)}
                                            </span>
                                        ))}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div
                    ref={footerRef}
                    className="w-full flex flex-row flex-wrap justify-end items-center gap-2"
                >
                    {results ? (
                        <Button onClick={onClose}>{t('common.done')}</Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={handleClose} disabled={pending}>
                                {t('common.cancel')}
                            </Button>
                            {canCreateTarget && (
                                <Button
                                    type="submit"
                                    leftIcon={<IconFolderPlus size={16} aria-hidden="true" />}
                                    isLoading={create.isPending}
                                    disabled={overLimit}
                                >
                                    {t('create.submit')}
                                </Button>
                            )}
                            {targets.length > 0 && (
                                <Button
                                    type="submit"
                                    leftIcon={<IconArrowsExchange size={16} aria-hidden="true" />}
                                    isLoading={move.isPending}
                                    disabled={overLimit}
                                >
                                    {t('move.submit', { count: resources.length })}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </form>
        </Modal>
    );
}
